import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { faPlay, faSpinner, faClock } from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';

interface WorkflowStatusCardProps {
  title: string;
  description: string;
  icon: IconDefinition;
  color: 'blue' | 'green' | 'yellow' | 'red' | 'purple' | 'gray';
  isActive: boolean;
  lastRun?: string;
  schedule?: string;
  onManualTrigger?: () => void;
  isProcessing?: boolean;
  customAction?: string;
}

const WorkflowStatusCard: React.FC<WorkflowStatusCardProps> = ({
  title,
  description,
  icon,
  color,
  isActive,
  lastRun,
  schedule,
  onManualTrigger,
  isProcessing = false,
  customAction = "Manuell starten"
}) => {
  const colorClasses = {
    blue: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      icon: 'text-blue-500',
      button: 'bg-blue-500 hover:bg-blue-600'
    },
    green: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      icon: 'text-green-500',
      button: 'bg-green-500 hover:bg-green-600'
    },
    yellow: {
      bg: 'bg-yellow-50',
      border: 'border-yellow-200',
      icon: 'text-yellow-500',
      button: 'bg-yellow-500 hover:bg-yellow-600'
    },
    red: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      icon: 'text-red-500',
      button: 'bg-red-500 hover:bg-red-600'
    },
    purple: {
      bg: 'bg-purple-50',
      border: 'border-purple-200',
      icon: 'text-purple-500',
      button: 'bg-purple-500 hover:bg-purple-600'
    },
    gray: {
      bg: 'bg-gray-50',
      border: 'border-gray-200',
      icon: 'text-gray-500',
      button: 'bg-gray-500 hover:bg-gray-600'
    }
  };

  const colors = colorClasses[color];

  const formatLastRun = (dateString?: string) => {
    if (!dateString) return 'Noch nie ausgeführt';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Vor weniger als 1 Stunde';
    if (diffHours < 24) return `Vor ${diffHours} Stunde${diffHours !== 1 ? 'n' : ''}`;
    
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `Vor ${diffDays} Tag${diffDays !== 1 ? 'en' : ''}`;
    
    return date.toLocaleDateString('de-DE');
  };

  const parseCronToHuman = (cronExpression?: string) => {
    if (!cronExpression) return 'Nicht geplant';
    
    // Einfache Cron-Parsing für häufige Muster
    const patterns: { [key: string]: string } = {
      '0 9 * * *': 'Täglich um 09:00',
      '0 8 * * 1': 'Montags um 08:00',
      '0 2 1 * *': 'Monatlich am 1. um 02:00',
      '0 7 1 1 *': 'Jährlich am 1. Januar um 07:00',
      '0 */6 * * *': 'Alle 6 Stunden'
    };
    
    return patterns[cronExpression] || cronExpression;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`${colors.bg} ${colors.border} border rounded-lg p-6 hover:shadow-md transition-shadow`}
    >
      {/* **HEADER** */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center">
          <div className={`w-12 h-12 ${colors.bg} rounded-lg flex items-center justify-center border ${colors.border}`}>
            <FontAwesomeIcon icon={icon} className={`text-xl ${colors.icon}`} />
          </div>
          <div className="ml-3">
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-600">{description}</p>
          </div>
        </div>
        
        <div className={`px-3 py-1 rounded-full text-xs font-medium ${
          isActive 
            ? 'bg-green-100 text-green-700' 
            : 'bg-gray-100 text-gray-600'
        }`}>
          {isActive ? 'Aktiv' : 'Inaktiv'}
        </div>
      </div>

      {/* **STATUS INFORMATION** */}
      <div className="space-y-2 mb-4">
        <div className="flex items-center text-sm text-gray-600">
          <FontAwesomeIcon icon={faClock} className="mr-2" />
          <span>Letzte Ausführung: {formatLastRun(lastRun)}</span>
        </div>
        
        {schedule && (
          <div className="text-sm text-gray-600">
            <span className="font-medium">Zeitplan:</span> {parseCronToHuman(schedule)}
          </div>
        )}
      </div>

      {/* **MANUAL TRIGGER BUTTON** */}
      {onManualTrigger && (
        <button
          onClick={onManualTrigger}
          disabled={isProcessing}
          className={`w-full px-4 py-2 text-white rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${colors.button}`}
        >
          {isProcessing ? (
            <>
              <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
              Wird ausgeführt...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faPlay} className="mr-2" />
              {customAction}
            </>
          )}
        </button>
      )}
    </motion.div>
  );
};

export default WorkflowStatusCard;